/**
 * Module-level bridge between individual `ui.pill.*` handlers and the
 * React `useChat` hook.
 *
 * Handlers live outside the React tree (they're a static map in handlers.ts)
 * so they can't call `setMessages` directly.  `useChat` registers a sink
 * once on mount; each pill handler invokes it when the LLM emits a pill.
 *
 * Only one sink may be registered at a time — the chat panel is a singleton
 * in the add-in.
 */

import type { Pill } from "../../../src/chat/types";

type PillSink = (pills: Pill[]) => void;

let sink: PillSink | null = null;

/** useChat calls this on mount; the closure typically appends pills to the
 *  last assistant message in state. */
export function registerPillSink(next: PillSink): () => void {
  sink = next;
  return () => {
    if (sink === next) sink = null;
  };
}

/** Handler calls this when the LLM emits a pill set. No-op if no chat panel
 *  is mounted (shouldn't happen in normal flows). */
export function emitPills(pills: Pill[]): void {
  sink?.(pills);
}
